import { useCallback, useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";
import { Box } from "@mui/material";
import { DotLegalFullScreenSpinner } from "@dotlegal/dotlegal-ui-components";
import { useDocumentPreviewStyles } from "./DocumentPreview.styles";
import DocumentPreviewToolbar from "./DocumentPreviewToolbar";
import { useDocumentKeyboardNavigation } from "./DocumentPreview.hooks";
import { useCtrlWheelZoom } from "./useCtrlWheelZoom";
import { IDocumentNavigation, IDocumentPreviewToolbarAction } from "./DocumentPreview.types";
import DocumentPreviewFailed from "./DocumentPreviewFailed";
import {
    buildSingleNeedleRegex,
    CITATION_OVERLAY_COLOR,
    CitationAnchors,
    deriveCitationAnchorSets,
    getBandRects,
    getMatchedSliceRects,
    findAnchorRangeInTextNodes,
    normalizeForSearch,
    unwrapMarks,
} from "./documentSearch";
import { grey } from "@mui/material/colors";
import TextSelectionLayer from "./textSelection/TextSelectionLayer";
import { WORD_TEXT_SELECTION_DOM_CONFIG } from "./textSelection/textSelection.domConfigs";
import { ITextSelectionConfig } from "./textSelection/textSelection.types";

const PAGE_SECTION_SELECTOR = "section.docx-preview";
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 2;
const SEARCH_MARK_ATTRIBUTE = "data-word-search-mark";
const CITATION_OVERLAY_CLASS = "citation-overlay-layer";

// Breathing room beside the page so its drop shadow is not clipped once the page is fitted to the viewer.
const PAGE_HORIZONTAL_MARGIN = 16;

export interface IWordViewerProps {
    documentData?: ArrayBuffer;
    isLoading: boolean;
    zoomLevel: number;
    zoomInputValue: string;
    onZoomBy?: (factor: number) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onZoomInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onZoomInputBlur: () => void;
    onDownload: () => void;
    isDownloading: boolean;
    documentNavigation?: IDocumentNavigation;
    toolbarAction?: IDocumentPreviewToolbarAction;
    initialPage?: number;
    initialSearchText?: string;
    initialMatchSnippet?: string;
    citationChunkContent?: string;
    onCitationResolved?: (found: boolean) => void;
    textSelection?: ITextSelectionConfig;
}

interface SnippetAnchor {
    before: string;
    mark: string;
    after: string;
}

const MIN_ANCHOR_LENGTH = 8;

// Splits the search-result snippet HTML into the text before/inside/after the <mark> tag so each
// side can be matched independently against rendered DOM text. The mark text itself isn't reliable
// — the search indexer normalizes punctuation away (e.g. underscores), so the rendered doc may
// say "frequency_penalty" while the snippet's <mark> reads "frequencypenalty". The before/after
// fragments around the mark usually survive that normalization and are what we actually anchor on.
//
// Example: input HTML 'Ja, både <mark>frequencypenalty</mark> og presencepenalty findes' →
//   { before: "Ja, både", mark: "frequencypenalty", after: "og presencepenalty findes" }
function parseSnippetAnchor(snippetHtml: string): SnippetAnchor | undefined {
    if (!snippetHtml) return undefined;
    const tmp = document.createElement("div");
    tmp.innerHTML = snippetHtml;
    const text = tmp.textContent ?? "";
    const mark = tmp.querySelector("mark");
    const markText = mark?.textContent ?? "";
    if (!markText) {
        const trimmed = text.trim();
        return trimmed ? { before: trimmed, mark: "", after: "" } : undefined;
    }
    const idx = text.indexOf(markText);
    if (idx < 0) {
        const trimmed = text.trim();
        return trimmed ? { before: trimmed, mark: markText, after: "" } : undefined;
    }
    return {
        before: text.substring(0, idx).trim(),
        mark: markText,
        after: text.substring(idx + markText.length).trim(),
    };
}

// Picks the docx-preview section (page) that contains the snippet, returning its 1-based index.
// We try progressively less-specific needles because there's no single needle that's both unique
// and guaranteed to match — the indexer drifts from the rendered doc in small ways (lost spaces,
// truncated tail words, normalized punctuation), so any one needle can miss. Compound needles
// (before+mark, mark+after) are tried first because they're the most discriminating; plain
// before/after second; truncated tails last as a "good enough" fallback.
//
// Example for snippet "Sæt <mark>frequency_penalty</mark> til 0.2. 2." (page 3 in the doc):
//   - "Sæt frequency_penalty" (before+mark) → matches page 3 ✓ — distinctive enough to skip page 2
//   - plain "Sæt" (before) would be too short (< MIN_ANCHOR_LENGTH) and would otherwise match
//     several "Sæt den til..." occurrences across pages
//   - plain "til 0.2. 2." (after) doesn't exist verbatim in the doc — the "2." is bleed-through
//     from the next heading, an indexer artifact
function findSectionForAnchor(sections: HTMLElement[], anchor: SnippetAnchor): number {
    const sectionTexts = sections.map((section) => normalizeForSearch(section.textContent ?? "").toLowerCase());

    const tryNeedle = (needle: string): number => {
        if (needle.length < MIN_ANCHOR_LENGTH) return -1;
        const normalized = normalizeForSearch(needle).toLowerCase();
        for (let i = 0; i < sectionTexts.length; i++) {
            if (sectionTexts[i].includes(normalized)) return i + 1;
        }
        return -1;
    };

    const candidates: string[] = [];
    if (anchor.before && anchor.mark) candidates.push(`${anchor.before} ${anchor.mark}`);
    if (anchor.mark && anchor.after) candidates.push(`${anchor.mark} ${anchor.after}`);
    if (anchor.after) candidates.push(anchor.after);
    if (anchor.before) candidates.push(anchor.before);
    // Tail truncations: when a long after/before drifts at its far end (e.g. snippet has
    // "...uden at øde" but doc continues with "ødelægge"), the half closer to the mark tends
    // to survive — keep the start of `after` and the end of `before`.
    if (anchor.after.length > 30) candidates.push(anchor.after.substring(0, 30));
    if (anchor.before.length > 30) candidates.push(anchor.before.substring(anchor.before.length - 30));

    for (const candidate of candidates) {
        const result = tryNeedle(candidate);
        if (result > 0) return result;
    }
    return -1;
}

// Wraps every regex match in this section in `<mark>` element(s). Matches that span multiple
// text nodes (e.g. a sentence with a bold-formatted phrase in the middle) are wrapped as
// one `<mark>` per text-node slice — adjacent marks render as a continuous yellow highlight
// even across element boundaries.
//
// Algorithm:
// 1. Snapshot every text node in document order and build a flat normalised string with
//    per-node offset bookkeeping. Snapshotting up front matters because surroundContents()
//    later splits text nodes, which would invalidate a live walker mid-iteration.
// 2. Run the regex against the flat string. Each match's flat-offset range may span any
//    number of consecutive text nodes.
// 3. Process matches in reverse so wrapping one slice doesn't shift the flat-string offsets
//    used by earlier matches. Within each match, also wrap node slices in reverse so a
//    multi-slice match inside the same parent doesn't invalidate node references.
// 4. Each wrap is a single-text-node range, which never throws cross-element errors —
//    surroundContents() only fails when the range itself crosses element boundaries.
function highlightMatchesInSection(section: HTMLElement, regex: RegExp): boolean {
    interface NodeEntry {
        node: Text;
        flatStart: number;
        flatEnd: number;
    }
    const walker = document.createTreeWalker(section, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;
            if (parent.tagName === "MARK" && parent.hasAttribute(SEARCH_MARK_ATTRIBUTE)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
        },
    });

    const entries: NodeEntry[] = [];
    let flatText = "";
    let textNode = walker.nextNode() as Text | null;
    while (textNode) {
        const value = textNode.nodeValue ?? "";
        entries.push({ node: textNode, flatStart: flatText.length, flatEnd: flatText.length + value.length });
        flatText += value;
        textNode = walker.nextNode() as Text | null;
    }
    if (entries.length === 0) return false;

    const normalized = normalizeForSearch(flatText);
    const matches: { start: number; end: number }[] = [];
    regex.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(normalized)) !== null) {
        if (match[0].length === 0) {
            regex.lastIndex++;
            continue;
        }
        matches.push({ start: match.index, end: match.index + match[0].length });
    }
    if (matches.length === 0) return false;

    let foundAny = false;
    for (let i = matches.length - 1; i >= 0; i--) {
        const { start, end } = matches[i];
        const overlapping = entries.filter((e) => e.flatEnd > start && e.flatStart < end);
        for (let j = overlapping.length - 1; j >= 0; j--) {
            const entry = overlapping[j];
            const localStart = Math.max(0, start - entry.flatStart);
            const localEnd = Math.min(entry.flatEnd - entry.flatStart, end - entry.flatStart);
            if (localStart >= localEnd) continue;
            try {
                const range = document.createRange();
                range.setStart(entry.node, localStart);
                range.setEnd(entry.node, localEnd);
                const mark = document.createElement("mark");
                mark.setAttribute(SEARCH_MARK_ATTRIBUTE, "true");
                range.surroundContents(mark);
                foundAny = true;
            } catch {
                // surroundContents only fails when a range crosses element boundaries — but every
                // range we construct here has both endpoints in the same text node, so this is just
                // defensive against unexpected DOM mutations.
            }
        }
    }
    return foundAny;
}

// Removes every citation overlay layer previously appended by renderCitationOverlay. Called
// before re-rendering on prop changes and when switching to searchbar mode (which uses <mark>
// instead of overlays).
function clearCitationOverlays(container: HTMLElement) {
    container.querySelectorAll(`.${CITATION_OVERLAY_CLASS}`).forEach((el) => el.remove());
}

function findContainingSection(node: Node, sectionSet: Set<HTMLElement>): HTMLElement | null {
    let cursor: Node | null = node.parentNode;
    while (cursor) {
        if (sectionSet.has(cursor as HTMLElement)) return cursor as HTMLElement;
        cursor = cursor.parentNode;
    }
    return null;
}

// Finds the start/end anchor positions in the rendered DOM, walks every text node between
// them in document order, groups them by docx-preview page section, and draws a layer of
// semi-transparent rectangle overlays in each section covering the line boxes the
// per-section sub-range produces. Returns the 1-based section numbers the overlays touched.
//
// Coordinates: getClientRects() returns post-transform viewport coords because the parent
// `containerRef` is scaled via `transform: scale(zoomLevel)`. Dividing the delta from the
// section's top-left by `zoomLevel` yields pre-transform offsets — those go into the overlay
// `<div>`'s inline style, and the container's transform then scales the overlay back to
// match the text. Net effect: the overlays follow text positions correctly at any zoom
// without needing to re-render on zoom change.
function renderCitationOverlay(container: HTMLElement, sections: HTMLElement[], anchors: CitationAnchors, zoomLevel: number): number[] {
    const position = findAnchorRangeInTextNodes(container, anchors);
    if (!position) return [];

    const sectionSet = new Set(sections);
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);

    let activeSection: HTMLElement | null = null;
    let activeStart: { node: Text; offset: number } | null = null;
    let activeEnd: { node: Text; offset: number } | null = null;
    const matchedSections = new Set<number>();
    let started = false;

    const flush = () => {
        if (!activeSection || !activeStart || !activeEnd) return;
        try {
            const activeRange = {
                startNode: activeStart.node,
                startOffset: activeStart.offset,
                endNode: activeEnd.node,
                endOffset: activeEnd.offset,
            };
            const rects = anchors.exact
                ? // A table row hugs its matched text: the cells beside it on the line are not cited.
                  getMatchedSliceRects(activeSection, activeRange)
                : getBandRects(activeSection, activeRange);
            if (rects.length > 0) {
                drawOverlayLayer(activeSection, rects, zoomLevel);
                const sectionIdx = sections.indexOf(activeSection);
                if (sectionIdx >= 0) matchedSections.add(sectionIdx + 1);
            }
        } catch {
            // Range construction can throw on detached/replaced nodes — skip.
        }
        activeSection = null;
        activeStart = null;
        activeEnd = null;
    };

    let textNode = walker.nextNode() as Text | null;
    while (textNode) {
        const isStartNode = textNode === position.startNode;
        const isEndNode = textNode === position.endNode;

        if (!started && !isStartNode) {
            textNode = walker.nextNode() as Text | null;
            continue;
        }
        if (!started) started = true;

        const startOffset = isStartNode ? position.startOffset : 0;
        const endOffset = isEndNode ? position.endOffset : (textNode.nodeValue?.length ?? 0);

        if (startOffset < endOffset) {
            const section = findContainingSection(textNode, sectionSet);
            if (section) {
                if (activeSection !== section) {
                    flush();
                    activeSection = section;
                    activeStart = { node: textNode, offset: startOffset };
                }
                activeEnd = { node: textNode, offset: endOffset };
            }
        }

        if (isEndNode) break;
        textNode = walker.nextNode() as Text | null;
    }
    flush();

    return [...matchedSections].sort((a, b) => a - b);
}

function drawOverlayLayer(section: HTMLElement, rects: DOMRect[], zoomLevel: number) {
    if (section.style.position !== "relative") section.style.position = "relative";
    const sectionRect = section.getBoundingClientRect();

    const layer = document.createElement("div");
    layer.className = CITATION_OVERLAY_CLASS;
    layer.style.cssText = "position: absolute; inset: 0; pointer-events: none; z-index: 5;";

    for (const r of rects) {
        const overlay = document.createElement("div");
        overlay.style.position = "absolute";
        overlay.style.top = `${(r.top - sectionRect.top) / zoomLevel}px`;
        overlay.style.left = `${(r.left - sectionRect.left) / zoomLevel}px`;
        overlay.style.width = `${r.width / zoomLevel}px`;
        overlay.style.height = `${r.height / zoomLevel}px`;
        overlay.style.background = CITATION_OVERLAY_COLOR;
        layer.appendChild(overlay);
    }

    section.appendChild(layer);
}

function findSectionsWithMatches(sections: HTMLElement[], regex: RegExp): number[] {
    const result: number[] = [];
    sections.forEach((section, index) => {
        const text = normalizeForSearch(section.textContent ?? "");
        regex.lastIndex = 0;
        if (regex.test(text)) {
            result.push(index + 1);
        }
    });
    return result;
}

export default function WordViewer(props: IWordViewerProps) {
    const styles = useDocumentPreviewStyles();
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    useCtrlWheelZoom(scrollContainerRef, props.onZoomBy);
    const pageSectionsRef = useRef<HTMLElement[]>([]);
    const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
    const isManualNavigationRef = useRef<boolean>(false);
    const manualNavigationTimeoutRef = useRef<number | null>(null);
    const lastScrolledCitationRef = useRef<string | undefined>(undefined);
    const lastResolvedCitationRef = useRef<string | undefined>(undefined);
    // Held in a ref rather than listed as a dependency: the callback is rebuilt on every parent render,
    // and the citation effect re-renders the overlays, so depending on it would loop.
    const onCitationResolvedRef = useRef(props.onCitationResolved);
    onCitationResolvedRef.current = props.onCitationResolved;
    const [hasError, setHasError] = useState(false);
    const [isRendering, setIsRendering] = useState(false);
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInputValue, setPageInputValue] = useState("1");
    const [searchText, setSearchText] = useState<string>(props.initialSearchText ?? "");
    const [debouncedSearchText, setDebouncedSearchText] = useState<string>(props.initialSearchText ?? "");
    const [pagesWithMatches, setPagesWithMatches] = useState<number[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(-1);
    const [viewerWidth, setViewerWidth] = useState(0);
    const [naturalPageSize, setNaturalPageSize] = useState<{ width: number; height: number }>();

    // docx-preview lays every page out at its real paper width, which is wider than the viewer whenever the
    // preview sits in a rail panel. 100% therefore means "the page fits the width available", the same thing
    // 100% already means in the pdf viewer, and the zoom multiplies from there. Fit only ever shrinks: a page
    // that already fits is left at its real size rather than magnified.
    const fitScale = naturalPageSize && viewerWidth > 0 ? Math.min(1, (viewerWidth - PAGE_HORIZONTAL_MARGIN) / naturalPageSize.width) : 1;
    const effectiveScale = fitScale * props.zoomLevel;
    const scaledPageSize = naturalPageSize ? { width: naturalPageSize.width * effectiveScale, height: naturalPageSize.height * effectiveScale } : undefined;

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) {
            return;
        }
        const resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width;
            if (width !== undefined) {
                setViewerWidth(width);
            }
        });
        resizeObserver.observe(scrollContainer);
        return () => resizeObserver.disconnect();
    }, []);

    useDocumentKeyboardNavigation(props.documentNavigation);

    useEffect(() => {
        setHasError(false);
    }, [props.documentData]);

    useEffect(() => {
        setSearchText(props.initialSearchText ?? "");
        setDebouncedSearchText(props.initialSearchText ?? "");
    }, [props.initialSearchText, props.documentData]);

    useEffect(() => {
        if (!props.documentData || !containerRef.current) {
            return;
        }
        const container = containerRef.current;
        setIsRendering(true);
        container.innerHTML = "";

        renderAsync(props.documentData, container, undefined, {
            className: "docx-preview",
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            breakPages: true,
            experimental: true,
            ignoreLastRenderedPageBreak: false,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            renderEndnotes: true,
        })
            .then(() => {
                const sections = Array.from(container.querySelectorAll<HTMLElement>(PAGE_SECTION_SELECTOR));
                pageSectionsRef.current = sections;
                setNaturalPageSize(sections.length > 0 ? { width: sections[0].offsetWidth, height: container.offsetHeight } : undefined);
                setNumPages(sections.length);
                setCurrentPage(1);
                setPageInputValue("1");
                setIsRendering(false);
                attachIntersectionObserver(sections);
            })
            .catch(() => {
                setHasError(true);
                setIsRendering(false);
            });

        return () => {
            intersectionObserverRef.current?.disconnect();
            intersectionObserverRef.current = null;
            if (manualNavigationTimeoutRef.current) {
                window.clearTimeout(manualNavigationTimeoutRef.current);
                manualNavigationTimeoutRef.current = null;
            }
        };
    }, [props.documentData]);

    function attachIntersectionObserver(sections: HTMLElement[]) {
        intersectionObserverRef.current?.disconnect();

        if (sections.length === 0) {
            intersectionObserverRef.current = null;
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (isManualNavigationRef.current) return;
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => sections.indexOf(a.target as HTMLElement) - sections.indexOf(b.target as HTMLElement));
                if (visible.length === 0) return;
                const topPageIndex = sections.indexOf(visible[0].target as HTMLElement);
                if (topPageIndex < 0) return;
                const pageNumber = topPageIndex + 1;
                setCurrentPage(pageNumber);
                setPageInputValue(String(pageNumber));
            },
            { threshold: [0, 0.25, 0.5] }
        );
        sections.forEach((section) => observer.observe(section));
        intersectionObserverRef.current = observer;
    }

    const goToPage = useCallback((pageNumber: number) => {
        const sections = pageSectionsRef.current;
        if (sections.length === 0) return;
        const validPage = Math.max(1, Math.min(pageNumber, sections.length));
        setCurrentPage(validPage);
        setPageInputValue(String(validPage));

        isManualNavigationRef.current = true;
        if (manualNavigationTimeoutRef.current) window.clearTimeout(manualNavigationTimeoutRef.current);
        manualNavigationTimeoutRef.current = window.setTimeout(() => {
            isManualNavigationRef.current = false;
        }, 500);

        sections[validPage - 1].scrollIntoView({ block: "start", behavior: "auto" });
    }, []);

    useEffect(() => {
        if (numPages > 0 && props.initialPage && props.initialPage <= numPages) {
            goToPage(props.initialPage);
        }
    }, [numPages, props.initialPage, goToPage]);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearchText(searchText), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        const container = containerRef.current;
        const sections = pageSectionsRef.current;
        if (!container) return;

        // Reported once per citation, and only once the document has rendered into sections: before that
        // "no match" only means the text isn't on screen yet.
        const reportCitationResolved = (found: boolean) => {
            if (!props.citationChunkContent || props.citationChunkContent === lastResolvedCitationRef.current) {
                return;
            }
            onCitationResolvedRef.current?.(found);
            lastResolvedCitationRef.current = props.citationChunkContent;
        };

        unwrapMarks(container, SEARCH_MARK_ATTRIBUTE);
        clearCitationOverlays(container);

        if (sections.length === 0) {
            setPagesWithMatches([]);
            setCurrentMatchIndex(-1);
            return;
        }

        // Searchbar mode: <mark> highlights via TreeWalker + Range.surroundContents. We auto-jump
        // to the first matched page because that matches user-initiated search intent.
        if (debouncedSearchText.trim().length >= MIN_SEARCH_LENGTH) {
            const regex = buildSingleNeedleRegex(debouncedSearchText);
            const matchingPages = findSectionsWithMatches(sections, regex);
            sections.forEach((section, index) => {
                if (matchingPages.includes(index + 1)) {
                    highlightMatchesInSection(section, regex);
                }
            });
            setPagesWithMatches(matchingPages);

            if (matchingPages.length === 0) {
                setCurrentMatchIndex(-1);
                return;
            }

            let initialMatchIndex = 0;
            const anchor = props.initialMatchSnippet ? parseSnippetAnchor(props.initialMatchSnippet) : undefined;
            if (anchor) {
                const anchorPage = findSectionForAnchor(sections, anchor);
                const anchorMatchIndex = anchorPage > 0 ? matchingPages.indexOf(anchorPage) : -1;
                if (anchorMatchIndex !== -1) initialMatchIndex = anchorMatchIndex;
            } else if (props.initialPage) {
                const pageMatchIndex = matchingPages.indexOf(props.initialPage);
                if (pageMatchIndex !== -1) initialMatchIndex = pageMatchIndex;
            }
            setCurrentMatchIndex(initialMatchIndex);
            goToPage(matchingPages[initialMatchIndex]);
            return;
        }

        // Citation mode: rectangle overlays via Range.getClientRects(). Auto-scroll fires once per
        // unique citation (guarded by lastScrolledCitationRef) so re-runs of this effect don't snap
        // the view back when the user has scrolled away. We need this here because docx citations
        // from AI chat carry no page hint (pageNumber: null), so the existing `[initialPage]` effect
        // can't do it.
        const anchorSets = deriveCitationAnchorSets(props.citationChunkContent);
        const matchingPages = [...new Set(anchorSets.flatMap((anchors) => renderCitationOverlay(container, sections, anchors, effectiveScale)))].sort(
            (a, b) => a - b
        );
        setPagesWithMatches(matchingPages);
        setCurrentMatchIndex(matchingPages.length === 0 ? -1 : 0);
        reportCitationResolved(matchingPages.length > 0);
        if (props.citationChunkContent && matchingPages.length > 0 && props.citationChunkContent !== lastScrolledCitationRef.current) {
            goToPage(matchingPages[0]);
            lastScrolledCitationRef.current = props.citationChunkContent;
        }
        // `effectiveScale` is intentionally excluded from deps: overlay coords are computed
        // as pre-transform offsets (delta / effectiveScale), which the container's `transform: scale`
        // then rescales to match text at any later zoom — no recompute needed. Including the
        // dep would unnecessarily re-run on every zoom click.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchText, props.citationChunkContent, numPages, props.initialPage, props.initialMatchSnippet, goToPage]);

    const handlePageInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPageInputValue(event.target.value);
    };

    const handlePageInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            const pageNumber = parseInt(pageInputValue, 10);
            if (!isNaN(pageNumber)) goToPage(pageNumber);
        }
    };

    const handlePageInputBlur = () => {
        const pageNumber = parseInt(pageInputValue, 10);
        if (!isNaN(pageNumber)) goToPage(pageNumber);
        else setPageInputValue(String(currentPage));
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const goToPreviousMatch = () => {
        if (pagesWithMatches.length === 0) return;
        const newIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : pagesWithMatches.length - 1;
        setCurrentMatchIndex(newIndex);
        goToPage(pagesWithMatches[newIndex]);
    };

    const goToNextMatch = () => {
        if (pagesWithMatches.length === 0) return;
        const newIndex = currentMatchIndex < pagesWithMatches.length - 1 ? currentMatchIndex + 1 : 0;
        setCurrentMatchIndex(newIndex);
        goToPage(pagesWithMatches[newIndex]);
    };

    return (
        <Box sx={styles.imageViewerContainer}>
            <DocumentPreviewToolbar
                zoomLevel={props.zoomLevel}
                zoomInputValue={props.zoomInputValue}
                onZoomIn={props.onZoomIn}
                onZoomOut={props.onZoomOut}
                onZoomInputChange={props.onZoomInputChange}
                onZoomInputKeyDown={props.onZoomInputKeyDown}
                onZoomInputBlur={props.onZoomInputBlur}
                onDownload={props.onDownload}
                isDownloading={props.isDownloading}
                documentNavigation={props.documentNavigation}
                toolbarAction={props.toolbarAction}
                pageSelector={
                    numPages > 0
                        ? {
                              currentPage,
                              numPages,
                              pageInputValue,
                              onPageInputChange: handlePageInputChange,
                              onPageInputKeyDown: handlePageInputKeyDown,
                              onPageInputBlur: handlePageInputBlur,
                              onPreviousPage: () => goToPage(currentPage - 1),
                              onNextPage: () => goToPage(currentPage + 1),
                          }
                        : undefined
                }
                searchProps={{
                    searchText,
                    onSearchChange: handleSearchChange,
                    matchCount: pagesWithMatches.length,
                    currentMatchIndex,
                    onPreviousMatch: goToPreviousMatch,
                    onNextMatch: goToNextMatch,
                }}
            />
            <Box sx={styles.wordViewerContainer} ref={scrollContainerRef}>
                {(props.isLoading || isRendering) && (
                    <Box sx={styles.loadingOverlay}>
                        <DotLegalFullScreenSpinner />
                    </Box>
                )}
                {hasError && <DocumentPreviewFailed />}
                {/* A `transform: scale` leaves the layout box at its unscaled size, so the scroll container
                    would neither shrink with the page nor let the user reach a zoomed-in page's right edge.
                    This wrapper carries the scaled size so scrolling and centring see what is on screen. */}
                <Box sx={{ width: scaledPageSize?.width, height: scaledPageSize?.height, flexShrink: 0 }}>
                    <Box
                        ref={containerRef}
                        sx={{
                            display: hasError ? "none" : "block",
                            backgroundColor: grey[100],
                            // Kept at the page's own width so the scale below has something of a fixed size to shrink; without it
                            // the block would take its width from the scaled wrapper and the two would chase each other.
                            width: naturalPageSize?.width,
                            transform: `scale(${effectiveScale})`,
                            transformOrigin: "top left",
                            "& .docx-preview-wrapper": {
                                background: "transparent !important",
                                paddingTop: 0,
                                paddingLeft: 0,
                                paddingRight: 0,
                                // docx-preview's own wrapper is a column flex box that centres its pages. Left
                                // unsafe, a page wider than the wrapper loses its left half to negative scroll
                                // space, which is what cropped the first characters of every line.
                                alignItems: "safe center",
                                "& .docx-preview": {
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                },
                            },
                        }}
                    />
                </Box>
                {props.textSelection && (
                    <TextSelectionLayer
                        config={props.textSelection}
                        rootRef={containerRef}
                        domConfig={WORD_TEXT_SELECTION_DOM_CONFIG}
                        resetKey={`word-${numPages}-${effectiveScale}`}
                    />
                )}
            </Box>
        </Box>
    );
}
